import { type ComponentType, useCallback, useEffect } from 'react';
import { useLocation } from '@direct-frontend/modules-router';

// Map'а элементов, подскролл к которым возможен
export const SCROLL_TO_ELEMENTS_MAP: { [key in string]: key } = {
    integrationsOrdersGranule: 'integrationsOrdersGranule',
    variants: 'variants',
    settingsDefaultDimensions: 'settingsDefaultDimensions',
    settingsAboutDescription: 'settingsAboutDescription',
    settingsAboutFavicon: 'settingsAboutFavicon',
    settingsAboutRequiredPages: 'settingsAboutRequiredPages',
    settingsSeoTemplates: 'settingsSeoTemplates',
    settingsIntegrationsYaWebmaster: 'settingsIntegrationsYaWebmaster',
    settingsIntegrationsGSC: 'settingsIntegrationsGSC',
    settingsIntegrationsRetailCRM: 'settingsIntegrationsRetailCRM',
    settingsIntegrationsBitrix24: 'settingsIntegrationsBitrix24',
    settingsIntegrationsAmoCRM: 'settingsIntegrationsAmoCRM',
    settingsIntegrationsJivo: 'settingsIntegrationsJivo',
    settingsIntegrationsAdmitad: 'settingsIntegrationsAdmitad',
    settingsIntegrationsMindbox: 'settingsIntegrationsMindbox',
    settingsIntegrationsYaDirect: 'settingsIntegrationsYaDirect',
    settingsIntegrationsYaMerchants: 'settingsIntegrationsYaMerchants',
    settingsIntegrationsYaMetrika: 'settingsIntegrationsYaMetrika',
    dashboardIndexPrices: 'dashboardIndexPrices',
    settingsOrderTruthfulLabel: 'settingsOrderTruthfulLabel',
    settingsPaymentOnline: 'settingsPaymentOnline',
} as const;
export type ScrollElement = keyof typeof SCROLL_TO_ELEMENTS_MAP;
export type ScrollNodeAttribute = {
    [key in typeof SCROLL_TO_SECTION_DATA_ATTR_NAME]: ScrollElement;
};

export interface WithScrollHOCProps {
    // Название элемента, по которому будет происходить поиск элемента на странице для подскролла
    elementToScroll?: string;
    scrollNodeAttributes?: ScrollNodeAttribute;
}

// Название data-аттрибута, который устанавливается элементу, подскролл к которому необходим
export const SCROLL_TO_SECTION_DATA_ATTR_NAME = 'data-element-to-scroll';

/*
 * HOC, который реализует автоподскролл к элементу при наличии одного из двух параметров:
 *
 * 1) соответствующего признака в state
 * 2) значения в hash странице
 */
export function withScrollToSection<P>(
    Component: ComponentType<P & WithScrollHOCProps>,
    getElementToScroll: (props: P) => string
) {
    return function useWithScrollToSectionFn(props: P & WithScrollHOCProps) {
        const location = useLocation() as unknown as Location & { state: WithScrollHOCProps };
        const elementToScrollState = location.state?.elementToScroll;
        const locationHash = location.hash.slice(1);

        const elementToScroll = getElementToScroll(props);

        const scrollToSection = useCallback(() => {
            const section = document.querySelector(`[${SCROLL_TO_SECTION_DATA_ATTR_NAME}=${elementToScroll}]`);

            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        }, [elementToScroll]);

        useEffect(() => {
            const isFoundSectionNameInHash = locationHash === elementToScroll;
            const isFoundSectionNameInLocationStateProps = elementToScrollState === elementToScroll;

            if (isFoundSectionNameInLocationStateProps || isFoundSectionNameInHash) {
                scrollToSection();
            }
        }, [elementToScroll, elementToScrollState, locationHash, scrollToSection]);

        return (
            <Component
                {...props}
                elementToScroll={elementToScroll}
                scrollNodeAttributes={getScrollNodeAttribute(elementToScroll)}
            />
        );
    };
}

// Node-аттрибут, который необходимо навесить на элемент, подскролл к которому возможен
export const getScrollNodeAttribute = (element: ScrollElement): ScrollNodeAttribute => {
    return {
        [SCROLL_TO_SECTION_DATA_ATTR_NAME]: element,
    };
};
